import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  fetchPodcasts,
  fetchAllEpisodes,
  fetchGlobalTopWords,
  fetchEpisodesByKeyword,
  fetchEpisodesBySearch,
  slugify,
  getPodcastColor,
  PALETTE,
  pageData,
} from '../api.js';
import { applyMeta, homeMeta } from '../seo.js';
import SpeakingBar from '../components/SpeakingBar.jsx';

const ROTATIONS = ['-1.5deg', '1deg', '-0.75deg', '1.5deg', '-1deg', '0.5deg'];

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatMs(ms) {
  if (ms == null) return '';
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

// ─── Skeletons ────────────────────────────────────────────────────────────────

function FeaturedSkeleton() {
  return (
    <div className="featured-episode featured-episode--skeleton" aria-hidden="true">
      <div className="skeleton" style={{ height: 14, width: '35%', borderRadius: 4 }} />
      <div className="skeleton" style={{ height: 22, width: '75%', borderRadius: 4, marginTop: 10 }} />
      <div className="skeleton" style={{ height: 10, width: '100%', borderRadius: 999, marginTop: 20 }} />
    </div>
  );
}

function LatestCardSkeleton() {
  return (
    <div className="latest-card" aria-hidden="true" style={{ pointerEvents: 'none' }}>
      <div className="skeleton" style={{ height: 11, width: '60%', borderRadius: 4 }} />
      <div className="skeleton" style={{ height: 13, width: '88%', borderRadius: 4, marginTop: 6 }} />
      <div className="skeleton" style={{ height: 8, width: '100%', borderRadius: 999, marginTop: 10 }} />
    </div>
  );
}

function PodcastCardSkeleton() {
  return (
    <div className="podcast-card" aria-hidden="true" style={{ pointerEvents: 'none' }}>
      <div className="podcast-card__art-wrap">
        <div className="skeleton" style={{ width: '100%', height: '100%' }} />
      </div>
      <div className="podcast-card__body">
        <div className="skeleton" style={{ height: 14, width: '72%', borderRadius: 4 }} />
        <div className="skeleton" style={{ height: 11, width: '45%', borderRadius: 4, marginTop: 4 }} />
      </div>
    </div>
  );
}

// ─── Featured episode hero ─────────────────────────────────────────────────────

function FeaturedEpisode({ episode, podcast, accentColor }) {
  if (!episode || !podcast) return null;
  const isPending = episode.status !== 'complete' || episode.host_pct == null;
  return (
    // FE-020: link to episode detail page, not podcast page
    <Link
      to={`/podcast/${podcast.podcast_uuid}/episode/${slugify(episode.title)}`}
      className="featured-episode"
      style={{ '--feat-accent': accentColor }}
      aria-label={`Featured: ${episode.title}`}
    >
      <div className="featured-episode__eyebrow">
        <span className="featured-episode__label">Featured episode</span>
        <span className="featured-episode__podcast">{podcast.name}</span>
      </div>
      <h2 className="featured-episode__title">{episode.title}</h2>
      <p className="featured-episode__date">{formatDate(episode.published_at)}</p>

      {isPending ? (
        <div className="featured-episode__bar">
          <div className="speaking-bar speaking-bar--scanning" role="status" aria-label="Analysing" />
          <p className="scanning-label">analysing...</p>
        </div>
      ) : (
        <div className="featured-episode__bar">
          <div className="featured-episode__pcts">
            <span className="featured-episode__pct-host" style={{ color: accentColor }}>
              {episode.host_pct?.toFixed(1)}%
              <small>host</small>
            </span>
            <span className="featured-episode__pct-guest">
              {episode.guest_pct?.toFixed(1)}%
              <small>guest</small>
            </span>
          </div>
          <SpeakingBar
            hostPct={episode.host_pct}
            guestPct={episode.guest_pct}
            hostLabel="Host"
            guestLabel="Guest"
            accentColor={accentColor}
          />
        </div>
      )}
    </Link>
  );
}

// ─── Latest episode card ───────────────────────────────────────────────────────

function LatestCard({ episode, podcast, accentColor }) {
  if (!episode || !podcast) return null;
  const isPending = episode.status !== 'complete' || episode.host_pct == null;

  return (
    // FE-020: link to episode detail page, not podcast page
    <Link
      to={`/podcast/${podcast.podcast_uuid}/episode/${slugify(episode.title)}`}
      className="latest-card"
      style={{ '--card-accent': accentColor }}
      aria-label={`${podcast.name} — ${episode.title}`}
    >
      <span className="latest-card__podcast">{podcast.name}</span>
      <span className="latest-card__title">{episode.title}</span>
      {isPending ? (
        <div className="speaking-bar speaking-bar--scanning" role="status" aria-label="Analysing" style={{ marginTop: 8 }} />
      ) : (
        <SpeakingBar
          hostPct={episode.host_pct}
          guestPct={episode.guest_pct}
          hostLabel="Host"
          accentColor={accentColor}
        />
      )}
    </Link>
  );
}

// ─── Podcast card ──────────────────────────────────────────────────────────────

function PodcastCard({ podcast, index, accentColor }) {
  const accent   = accentColor ?? getPodcastColor(podcast.podcast_uuid);
  const rotation = ROTATIONS[index % ROTATIONS.length];
  const hasEpisodes = podcast.episode_count != null && podcast.episode_count > 0;
  const [imgError, setImgError] = React.useState(false);

  return (
    <Link
      to={`/podcast/${podcast.podcast_uuid}`}
      className="podcast-card"
      aria-label={`${podcast.name}${hasEpisodes ? ` — ${podcast.episode_count} episodes` : ''}`}
      style={{ '--card-accent': accent, '--card-rotation': rotation }}
    >
      <div className="podcast-card__art-wrap">
        {podcast.artwork_url && !imgError ? (
          <img
            src={podcast.artwork_url}
            alt=""
            className="podcast-card__art"
            width={240}
            height={240}
            loading="lazy"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="podcast-card__art--placeholder" aria-hidden="true">
            {podcast.name?.[0] ?? '?'}
          </div>
        )}
      </div>
      <div className="podcast-card__body">
        <span className="podcast-card__title">{podcast.name}</span>
        {podcast.host_name && (
          <span className="podcast-card__host">{podcast.host_name}</span>
        )}
        {hasEpisodes && (
          <span className="podcast-card__meta">
            {podcast.episode_count} episode{podcast.episode_count !== 1 ? 's' : ''}
          </span>
        )}
      </div>
    </Link>
  );
}

// ─── Compact episode card (used in keyword/search results) ────────────────────

function EpisodeResultCard({ episode, accentColor }) {
  const isPending = episode.status !== 'complete' || episode.host_pct == null;
  const topWords = (episode.top_words ?? []).slice(0, 5).map(w => w.word).filter(Boolean);
  const color = accentColor ?? getPodcastColor(episode.podcast_uuid);

  return (
    <Link
      to={`/podcast/${episode.podcast_uuid}/episode/${slugify(episode.title)}`}
      className="episode-result-card"
      style={{ '--ep-accent': color }}
      aria-label={`${episode.podcast_name} — ${episode.title}`}
    >
      <span className="episode-result-card__podcast">{episode.podcast_name}</span>
      <span className="episode-result-card__title">{episode.title}</span>
      <div className="episode-result-card__footer">
        {!isPending && episode.host_pct != null && (
          <span className="episode-result-card__pct">
            {episode.host_pct.toFixed(1)}% host
          </span>
        )}
        {topWords.length > 0 && (
          <span className="episode-result-card__words">
            {topWords.join(' · ')}
          </span>
        )}
      </div>
    </Link>
  );
}

// ─── Home ──────────────────────────────────────────────────────────────────────

export default function Home() {
  // The catalogue grid is the content worth prerendering, so only `podcasts`
  // and `topWords` are inlined. The full episode list is the whole 2 MB of
  // data.json and stays on the async path; the featured-episode block picks at
  // random, so leaving it empty on the first render is also what keeps the
  // server and client trees identical.
  const boot = pageData('home');

  const [podcasts,       setPodcasts]       = useState(boot?.podcasts ?? []);
  const [episodes,       setEpisodes]       = useState([]);
  const [loading,        setLoading]        = useState(!boot?.podcasts);
  const [error,          setError]          = useState(null);
  const [search,         setSearch]         = useState('');

  // Task A — trending topics
  const [topWords,       setTopWords]       = useState(boot?.topWords ?? []);
  const [activePill,     setActivePill]     = useState(null);
  const [pillResults,    setPillResults]    = useState(null);
  const [pillLoading,    setPillLoading]    = useState(false);

  // Task B — cross-episode search results
  const [episodeResults, setEpisodeResults] = useState(null);
  const [searchLoading,  setSearchLoading]  = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const searchTimerRef = useRef(null);

  useEffect(() => {
    if (location.state?.resetHome) {
      setSearch('');
      setActivePill(null);
      setPillResults(null);
    }
  }, [location.state]);

  // Stable featured index — pick once per mount, never re-randomize
  const featuredIdxRef = useRef(null);

  // Dynamic meta tags for catalogue page
  useEffect(() => applyMeta(homeMeta()), []);

  useEffect(() => {
    let cancelled = false;
    Promise.all([fetchPodcasts(), fetchAllEpisodes(), fetchGlobalTopWords(5)])
      .then(([{ podcasts: ps }, eps, words]) => {
        if (cancelled) return;
        setPodcasts(ps);
        setEpisodes(eps);
        setTopWords(words);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('Failed to load podcasts:', err);
        setError(true);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  // Build a podcast lookup map
  const podcastMap = useMemo(() => {
    const m = {};
    for (const p of podcasts) m[p.podcast_uuid] = p;
    return m;
  }, [podcasts]);

  // Featured: one random complete episode, stable across re-renders
  const featuredEpisode = useMemo(() => {
    const complete = episodes.filter(e => e.status === 'complete' && e.host_pct != null);
    if (!complete.length) return null;
    if (featuredIdxRef.current === null) {
      featuredIdxRef.current = Math.floor(Math.random() * complete.length);
    }
    return complete[featuredIdxRef.current % complete.length];
  }, [episodes]);

  const featuredPodcast = featuredEpisode ? podcastMap[featuredEpisode.podcast_uuid] : null;
  const featuredAccent  = getPodcastColor(featuredPodcast?.podcast_uuid);

  // Latest: 5 most-recent complete episodes, excluding the featured one
  const latestEpisodes = useMemo(() => {
    return episodes
      .filter(e => e.status === 'complete' && e.host_pct != null && e !== featuredEpisode)
      .sort((a, b) => (b.published_at ?? '') > (a.published_at ?? '') ? 1 : -1)
      .slice(0, 5);
  }, [episodes, featuredEpisode]);

  // Search filters podcast grid; also triggers cross-episode search
  const filteredPodcasts = useMemo(() => {
    if (!search.trim()) return podcasts;
    const q = search.toLowerCase();
    return podcasts.filter(p =>
      p.name?.toLowerCase().includes(q) || p.host_name?.toLowerCase().includes(q)
    );
  }, [podcasts, search]);

  // Determine if search has a value and no full podcast match
  const searchHasValue = search.trim().length > 0;

  // Cross-episode search — debounced
  useEffect(() => {
    if (!searchHasValue) {
      setEpisodeResults(null);
      return;
    }
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await fetchEpisodesBySearch(search.trim());
        setEpisodeResults(results);
      } catch {
        setEpisodeResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
    return () => clearTimeout(searchTimerRef.current);
  }, [search, searchHasValue]);

  // Task A — pill click
  const handlePillClick = async (word) => {
    if (activePill === word) {
      setActivePill(null);
      setPillResults(null);
      return;
    }
    setActivePill(word);
    setPillLoading(true);
    try {
      const results = await fetchEpisodesByKeyword(word);
      setPillResults(results);
    } catch {
      setPillResults([]);
    } finally {
      setPillLoading(false);
    }
  };

  const handleSearchKey = (e) => {
    // FE-005: navigate when search has a value and exactly one podcast matched
    if (e.key === 'Enter' && filteredPodcasts.length === 1 && searchHasValue) {
      navigate(`/podcast/${filteredPodcasts[0].podcast_uuid}`);
    }
  };

  // Decide what the main content area shows
  // Priority: pill active > search results > podcast grid
  const showPillResults = activePill !== null;
  const showSearchEpisodes = searchHasValue && !showPillResults;

  return (
    <div>
      {/* Search */}
      <div className="home-search">
        <input
          className="home-search__input"
          type="search"
          placeholder="Search podcasts, hosts or topics..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleSearchKey}
          aria-label="Search podcasts, hosts or topics"
        />
      </div>

      {error && (
        <div className="state-box" role="alert">
          <span className="state-box__icon" aria-hidden="true">!</span>
          <p className="state-box__heading">Could not load data</p>
          <p className="state-box__body">Failed to load podcasts. Please try again.</p>
        </div>
      )}

      {/* Top words */}
      {!error && !loading && topWords.length > 0 && (
        <section className="most-mentioned-section" aria-label="Top words across all episodes">
          <h2 className="most-mentioned-heading">Top words across all episodes</h2>
          <div className="most-mentioned-pills" role="group" aria-label="Top word filters">
            {topWords.map((word, i) => {
              const isActive = activePill === word;
              return (
                <button
                  key={word}
                  type="button"
                  style={{ '--mm-color': PALETTE[i % PALETTE.length] }}
                  className={`most-mentioned-pill${isActive ? ' most-mentioned-pill--active' : ''}`}
                  onClick={() => handlePillClick(word)}
                  aria-pressed={isActive}
                  aria-label={`${isActive ? 'Clear' : 'Show episodes about'}: ${word}`}
                >
                  <span className="most-mentioned-pill__rank">0{i + 1}</span>
                  <span className="most-mentioned-pill__word">{word}</span>
                  {isActive && <span className="pill__remove" aria-hidden="true">×</span>}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Featured episode */}
      {!error && !showPillResults && !showSearchEpisodes && (
        <section aria-label="Featured episode">
          {loading
            ? <FeaturedSkeleton />
            : <FeaturedEpisode episode={featuredEpisode} podcast={featuredPodcast} accentColor={featuredAccent} />
          }
        </section>
      )}

      {/* Latest added */}
      {!error && !showPillResults && !showSearchEpisodes && (
        <section className="latest-section" aria-label="Latest episodes">
          <h2 className="section-label">Latest added</h2>
          <div className="latest-strip">
            {loading
              ? Array.from({ length: 5 }, (_, i) => <LatestCardSkeleton key={i} />)
              : latestEpisodes.map((ep, i) => (
                  <LatestCard
                    key={ep.episode_uuid}
                    episode={ep}
                    podcast={podcastMap[ep.podcast_uuid]}
                    accentColor={getPodcastColor(ep.podcast_uuid)}
                  />
                ))
            }
          </div>
        </section>
      )}

      {/* Pill results panel — Task A */}
      {!error && showPillResults && (
        <section aria-label={`Episodes about ${activePill}`} aria-live="polite">
          <div className="results-panel-header">
            <h2 className="section-label">
              Episodes about &ldquo;{activePill}&rdquo;
            </h2>
            <button
              type="button"
              className="results-panel-close"
              onClick={() => { setActivePill(null); setPillResults(null); }}
              aria-label="Close topic results"
            >
              &times; clear
            </button>
          </div>
          {pillLoading ? (
            <div className="episode-result-grid">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="episode-result-card" aria-hidden="true">
                  <div className="skeleton" style={{ height: 11, width: '50%', borderRadius: 4 }} />
                  <div className="skeleton" style={{ height: 14, width: '88%', borderRadius: 4, marginTop: 6 }} />
                </div>
              ))}
            </div>
          ) : pillResults?.length === 0 ? (
            <div className="state-box">
              <p className="state-box__heading">No episodes found for &ldquo;{activePill}&rdquo;</p>
            </div>
          ) : (
            <div className="episode-result-grid">
              {(pillResults ?? []).map((ep, i) => (
                <EpisodeResultCard
                  key={ep.episode_uuid}
                  episode={ep}
                  accentColor={getPodcastColor(ep.podcast_uuid)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Search results — Task B */}
      {!error && showSearchEpisodes && (
        <section aria-label={`Search results for ${search}`} aria-live="polite">
          {/* Matching podcasts first */}
          {filteredPodcasts.length > 0 && (
            <>
              <p className="section-label" style={{ marginBottom: '1rem' }}>
                {filteredPodcasts.length} podcast{filteredPodcasts.length !== 1 ? 's' : ''} matching &ldquo;{search}&rdquo;
              </p>
              <div className="podcast-grid" style={{ marginBottom: '2rem' }}>
                {filteredPodcasts.map((p, i) => (
                  <PodcastCard key={p.podcast_uuid} podcast={p} index={i} />
                ))}
              </div>
            </>
          )}

          {/* Cross-episode results */}
          <p className="section-label" style={{ marginBottom: '1rem' }}>
            Episodes mentioning &ldquo;{search}&rdquo;
          </p>
          {searchLoading ? (
            <div className="episode-result-grid">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} className="episode-result-card" aria-hidden="true">
                  <div className="skeleton" style={{ height: 11, width: '50%', borderRadius: 4 }} />
                  <div className="skeleton" style={{ height: 14, width: '88%', borderRadius: 4, marginTop: 6 }} />
                </div>
              ))}
            </div>
          ) : episodeResults?.length === 0 ? (
            <div className="state-box">
              <p className="state-box__heading">No episodes match &ldquo;{search}&rdquo;</p>
            </div>
          ) : (
            <div className="episode-result-grid">
              {(episodeResults ?? []).map((ep, i) => (
                <EpisodeResultCard
                  key={ep.episode_uuid}
                  episode={ep}
                  accentColor={getPodcastColor(ep.podcast_uuid)}
                />
              ))}
            </div>
          )}
        </section>
      )}

      {/* Podcast grid — shown only when no search/pill is active */}
      {!error && !showPillResults && !showSearchEpisodes && (
        <section aria-label="Podcast catalogue">
          <div className="podcast-grid" aria-busy={loading}>
            {loading
              ? Array.from({ length: 8 }, (_, i) => <PodcastCardSkeleton key={i} />)
              : filteredPodcasts.map((p, i) => (
                  <PodcastCard key={p.podcast_uuid} podcast={p} index={i} />
                ))
            }
          </div>
        </section>
      )}
    </div>
  );
}
